import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import AnuncioBanner from '@/components/AnuncioBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Plus, Users, Trophy, Sparkles, X, Loader2, Wifi, Copy, RefreshCw, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import confetti from 'canvas-confetti';

const CATEGORIAS = [
  { id: 'clasico', nombre: 'Words Clásico', emoji: '📚', desc: 'Palabras variadas para todos' },
  { id: 'ninos', nombre: 'Words Niños', emoji: '🎈', desc: 'Palabras fáciles y divertidas' },
  { id: 'hot', nombre: 'Words Hot', emoji: '🔥', desc: 'Palabras para adultos' }
];

// URLs de sonidos
const SOUNDS = {
  thinking: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3',
  reveal: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
  master: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3'
};

const playSound = (type) => {
  try {
    const audio = new Audio(SOUNDS[type]);
    audio.volume = 0.5;
    audio.play().catch((err) => console.log('Sound error:', err));
  } catch (e) { console.log('Sound exception:', e); }
};

function Words() {
  const { user } = useAuth();
  const [showCrear, setShowCrear] = useState(false);
  const [showOnline, setShowOnline] = useState(false);
  const [showReglas, setShowReglas] = useState(false);
  const [partidas, setPartidas] = useState([]);
  const [partidaActual, setPartidaActual] = useState(null);
  const [palabraActual, setPalabraActual] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [juzgando, setJuzgando] = useState(false);
  const [showReveal, setShowReveal] = useState(false); // Revelación final épica
  
  // Online state
  const [salaOnline, setSalaOnline] = useState(null);
  const [codigoSala, setCodigoSala] = useState('');
  const [nombreJugador, setNombreJugador] = useState('');
  const [miRespuesta, setMiRespuesta] = useState('');
  const [polling, setPolling] = useState(false);
  
  // Formulario nueva partida
  const [formData, setFormData] = useState({
    categoria: 'clasico',
    num_rondas: 5,
    jugadores: ['', '']
  });

  useEffect(() => {
    fetchPartidas();
  }, []);

  const fetchPartidas = async () => {
    try {
      const res = await axios.get(`${API}/words/mis-partidas`);
      setPartidas(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const eliminarPartida = async (gameId) => {
    if (!window.confirm('¿Eliminar esta partida?')) return;
    try {
      await axios.delete(`${API}/words/partida/${gameId}`);
      fetchPartidas();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleCrearPartida = async () => {
    const jugadoresValidos = formData.jugadores.filter(j => j.trim());
    if (jugadoresValidos.length < 2) {
      alert('Necesitas al menos 2 jugadores');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/words/nueva-partida`, {
        categoria: formData.categoria,
        num_rondas: formData.num_rondas,
        jugadores: jugadoresValidos
      });
      
      setShowCrear(false);
      await fetchPartidas();
      // Iniciar partida automáticamente
      await iniciarPartida(res.data.id);
    } catch (error) {
      alert('Error al crear partida: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const iniciarPartida = async (gameId) => {
    setLoading(true);
    try {
      const partidaRes = await axios.get(`${API}/words/partida/${gameId}`);
      setPartidaActual(partidaRes.data);
      
      // Obtener palabra
      const palabraRes = await axios.get(`${API}/words/palabra/${gameId}`);
      setPalabraActual(palabraRes.data);
      
      // Inicializar respuestas vacías
      const resps = {};
      Object.keys(partidaRes.data.jugadores).forEach(j => {
        resps[j] = '';
      });
      setRespuestas(resps);
      setResultado(null);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleJuzgar = async () => {
    const respuestasValidas = Object.entries(respuestas)
      .filter(([_, palabra]) => palabra.trim())
      .map(([jugador, palabra]) => ({ jugador, palabra: palabra.trim() }));

    if (respuestasValidas.length < 2) {
      alert('Al menos 2 jugadores deben responder');
      return;
    }

    setJuzgando(true);
    // 🎵 Música de intriga mientras piensa
    playSound('thinking');
    
    try {
      const res = await axios.post(`${API}/words/juzgar`, {
        game_id: partidaActual.id,
        palabra_original: palabraActual.palabra,
        respuestas: respuestasValidas
      });
      
      // 🎵 Música de revelación
      playSound('reveal');
      
      setResultado(res.data);
      
      // Celebraciones especiales
      const analisis = res.data.analisis || [];
      const hayMaster = analisis.some(a => a.rango_especial === 'MASTER OF WORDS');
      const hayCaballero = analisis.some(a => a.rango_especial === 'CABALLERO WORDS');
      
      if (hayMaster) {
        // 🎵 Música épica para Master
        playSound('master');
        // Celebración épica para Master of Words
        const duration = 5 * 1000;
        const end = Date.now() + duration;
        const colors = ['#FFD700', '#FFA500', '#FF6347', '#9400D3'];
        (function frame() {
          confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
          confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
          if (Date.now() < end) requestAnimationFrame(frame);
        }());
      } else if (hayCaballero) {
        // Celebración para Caballero Words
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#FFD700', '#C0C0C0'] });
      }
      
      // Si la partida continúa, actualizar estado
      if (!res.data.partida_terminada) {
        setPartidaActual(prev => ({
          ...prev,
          jugadores: res.data.puntuaciones,
          ronda_actual: res.data.siguiente_ronda
        }));
      } else {
        setPartidaActual(prev => ({
          ...prev,
          jugadores: res.data.puntuaciones,
          estado: 'terminada',
          ganador_final: res.data.ganador_final
        }));
        // REVELACIÓN ÉPICA - Mostrar pantalla de expectación
        setShowReveal(true);
        
        // Después de 3 segundos, mostrar ganador con confeti y globos
        setTimeout(() => {
          // Confeti masivo
          const duration = 5 * 1000;
          const animationEnd = Date.now() + duration;
          const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
          
          (function frame() {
            confetti({
              particleCount: 7,
              angle: 60,
              spread: 55,
              origin: { x: 0 },
              colors: colors
            });
            confetti({
              particleCount: 7,
              angle: 120,
              spread: 55,
              origin: { x: 1 },
              colors: colors
            });
            // Globos desde abajo
            confetti({
              particleCount: 3,
              angle: 90,
              spread: 80,
              origin: { x: Math.random(), y: 1 },
              gravity: -0.5,
              scalar: 2,
              colors: colors
            });
            if (Date.now() < animationEnd) {
              requestAnimationFrame(frame);
            }
          }());
          
          playSound('master');
          setShowReveal(false);
        }, 3000);
      }
    } catch (error) {
      alert('Error al juzgar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setJuzgando(false);
    }
  };

  const siguienteRonda = async () => {
    try {
      const palabraRes = await axios.get(`${API}/words/palabra/${partidaActual.id}`);
      setPalabraActual(palabraRes.data);
      
      // Reset respuestas
      const resps = {};
      Object.keys(partidaActual.jugadores).forEach(j => {
        resps[j] = '';
      });
      setRespuestas(resps);
      setResultado(null);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const addJugador = () => {
    if (formData.jugadores.length < 8) {
      setFormData({ ...formData, jugadores: [...formData.jugadores, ''] });
    }
  };

  const removeJugador = (index) => {
    if (formData.jugadores.length > 2) {
      setFormData({ 
        ...formData, 
        jugadores: formData.jugadores.filter((_, i) => i !== index) 
      });
    }
  };

  const cerrarPartida = () => {
    setPartidaActual(null);
    setPalabraActual(null);
    setResultado(null);
    setRespuestas({});
    fetchPartidas();
  };

  // ========== FUNCIONES ONLINE ==========
  
  const crearSalaOnline = async () => {
    if (!nombreJugador.trim()) {
      alert('Escribe tu nombre');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/words/online/crear`, {
        categoria: formData.categoria,
        num_rondas: formData.num_rondas,
        nombre_host: nombreJugador.trim()
      });
      setCodigoSala(res.data.codigo);
      await actualizarSala(res.data.codigo);
      setShowOnline(false);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const unirseSala = async () => {
    if (!codigoSala.trim() || !nombreJugador.trim()) {
      alert('Introduce código y nombre');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/words/online/unirse`, {
        codigo_sala: codigoSala.toUpperCase(),
        nombre_jugador: nombreJugador.trim()
      });
      await actualizarSala(codigoSala.toUpperCase());
      setShowOnline(false);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const actualizarSala = useCallback(async (codigo) => {
    try {
      const res = await axios.get(`${API}/words/online/sala/${codigo || codigoSala}`);
      setSalaOnline(res.data);
    } catch (error) {
      console.error('Error actualizando sala:', error);
    }
  }, [codigoSala]);

  const iniciarPartidaOnline = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/words/online/iniciar/${codigoSala}`);
      await actualizarSala();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const enviarRespuestaOnline = async () => {
    if (!miRespuesta.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/words/online/responder`, {
        codigo_sala: codigoSala,
        nombre_jugador: nombreJugador,
        palabra: miRespuesta.trim()
      });
      setMiRespuesta('');
      await actualizarSala();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const juzgarOnline = async () => {
    setJuzgando(true);
    playSound('thinking'); // Sonido de pensando
    try {
      const res = await axios.post(`${API}/words/online/juzgar/${codigoSala}`);
      await actualizarSala();
      setResultado(res.data.resultado);
      
      // Sonido de revelación y confeti si hay ganador
      setTimeout(() => {
        playSound('reveal');
        if (res.data.resultado?.ganador) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4']
          });
        }
      }, 500);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setJuzgando(false);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard.writeText(codigoSala);
    alert('Código copiado: ' + codigoSala);
  };

  const cerrarSalaOnline = () => {
    setSalaOnline(null);
    setCodigoSala('');
    setNombreJugador('');
    setMiRespuesta('');
    setResultado(null);
  };

  // Polling para actualizar sala online
  useEffect(() => {
    if (salaOnline && salaOnline.estado !== 'terminada') {
      const interval = setInterval(() => actualizarSala(), 3000);
      return () => clearInterval(interval);
    }
  }, [salaOnline, actualizarSala]);

  // ========== VISTA SALA ONLINE ==========
  if (salaOnline) {
    const esHost = salaOnline.host === nombreJugador;
    const categoria = CATEGORIAS.find(c => c.id === salaOnline.categoria);
    const yaRespondi = salaOnline.respuestas_ronda && salaOnline.respuestas_ronda[nombreJugador];
    
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar />
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Wifi className="h-5 w-5 text-green-400" />
              <span className="text-green-400 text-sm">Online</span>
            </div>
            <h1 className="text-2xl font-bold text-gold">{categoria?.emoji} Words Online</h1>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="bg-gray-800 px-4 py-2 rounded-lg font-mono text-xl text-white">{codigoSala}</span>
              <Button size="sm" variant="ghost" onClick={copiarCodigo}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Jugadores - Puntuaciones con colores vibrantes */}
          <Card className="bg-gray-900 border-gray-700 mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-400 text-sm">Jugadores ({Object.keys(salaOnline.jugadores).length})</p>
                <Button size="sm" variant="ghost" onClick={() => actualizarSala()}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(salaOnline.jugadores)
                  .sort(([,a], [,b]) => b.puntos - a.puntos)
                  .map(([j, stats], index) => (
                  <div 
                    key={j} 
                    className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 ${
                      j === salaOnline.host 
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold shadow-lg shadow-yellow-500/30' 
                        : index === 0 && stats.puntos > 0
                          ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold'
                          : 'bg-gray-600 text-white'
                    }`}
                  >
                    {j === salaOnline.host && <span>👑</span>}
                    {index === 0 && stats.puntos > 0 && j !== salaOnline.host && <span>🔥</span>}
                    <span className="font-semibold">{j}</span>
                    <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-black/30 text-white font-bold">
                      {stats.puntos} pts
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Estado: Esperando */}
          {salaOnline.estado === 'esperando' && (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-6 text-center">
                <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Esperando jugadores...</p>
                <p className="text-white mb-4">Comparte el código: <strong className="text-gold">{codigoSala}</strong></p>
                {esHost && Object.keys(salaOnline.jugadores).length >= 2 && (
                  <Button className="btn-primary-mk" onClick={iniciarPartidaOnline} disabled={loading}>
                    {loading ? <Loader2 className="animate-spin" /> : '¡Empezar Partida!'}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Estado: Jugando */}
          {salaOnline.estado === 'jugando' && (
            <>
              <Card className="bg-gradient-to-br from-purple-900 to-indigo-900 border-purple-500 mb-4">
                <CardContent className="p-6 text-center">
                  <p className="text-purple-300 text-sm mb-1">Ronda {salaOnline.ronda_actual}/{salaOnline.num_rondas}</p>
                  <h2 className="text-4xl font-bold text-white uppercase">{salaOnline.palabra_actual}</h2>
                </CardContent>
              </Card>

              {/* Mi respuesta */}
              {!yaRespondi ? (
                <Card className="bg-gray-900 border-gray-700 mb-4">
                  <CardContent className="p-4">
                    <Label className="text-white mb-2 block">Tu respuesta:</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={miRespuesta}
                        onChange={(e) => setMiRespuesta(e.target.value)}
                        placeholder="Escribe tu palabra..."
                        className="bg-gray-800 border-gray-600"
                        onKeyPress={(e) => e.key === 'Enter' && enviarRespuestaOnline()}
                      />
                      <Button onClick={enviarRespuestaOnline} disabled={loading}>
                        {loading ? <Loader2 className="animate-spin h-4 w-4" /> : 'Enviar'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-green-900/30 border-green-700 mb-4">
                  <CardContent className="p-4 text-center">
                    <p className="text-green-400">✓ Respuesta enviada</p>
                    <p className="text-gray-400 text-sm">Esperando a los demás...</p>
                  </CardContent>
                </Card>
              )}

              {/* Estado de respuestas */}
              <Card className="bg-gray-900 border-gray-700 mb-4">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-sm mb-2">Respuestas: {Object.keys(salaOnline.respuestas_ronda || {}).length}/{Object.keys(salaOnline.jugadores).length}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(salaOnline.jugadores).map(j => (
                      <span key={j} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${salaOnline.respuestas_ronda?.[j] ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}>
                        {salaOnline.respuestas_ronda?.[j] ? '✓ ' : '⏳ '}{j}
                      </span>
                    ))}
                  </div>
                  
                  {esHost && Object.keys(salaOnline.respuestas_ronda || {}).length >= 2 && (
                    <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700" onClick={juzgarOnline} disabled={juzgando}>
                      {juzgando ? <><Loader2 className="animate-spin mr-2" /> Juzgando...</> : <><Sparkles className="mr-2" /> Juzgar Respuestas</>}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Resultado */}
          {salaOnline.resultado_actual && (
            <Card className="bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 border-2 border-yellow-500 mb-4 shadow-2xl shadow-purple-500/30">
              <CardHeader className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-t-lg">
                <CardTitle className="text-white text-center text-2xl flex items-center justify-center gap-2">
                  <Sparkles className="h-6 w-6" /> ¡Resultado de la Ronda! <Sparkles className="h-6 w-6" />
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Ganador en GRANDE */}
                <div className="flex flex-col items-center gap-6 mb-6">
                  {salaOnline.resultado_actual.ganador && (
                    <div className="text-center bg-gradient-to-br from-yellow-600/30 to-orange-600/30 rounded-2xl p-6 border-2 border-yellow-500 w-full transform hover:scale-105 transition-transform">
                      <p className="text-6xl mb-2">🥇</p>
                      <p className="text-yellow-400 font-bold text-3xl mb-1">{salaOnline.resultado_actual.ganador}</p>
                      <p className="text-yellow-300/80 text-lg">
                        {salaOnline.respuestas_ronda?.[salaOnline.resultado_actual.ganador] && (
                          <span className="bg-yellow-600/30 px-3 py-1 rounded-full">
                            "{salaOnline.respuestas_ronda[salaOnline.resultado_actual.ganador]}"
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* Segundo lugar más pequeño */}
                  {salaOnline.resultado_actual.segundo && (
                    <div className="text-center bg-gray-800/50 rounded-xl p-4 border border-gray-600 w-2/3 opacity-80">
                      <p className="text-3xl mb-1">🥈</p>
                      <p className="text-gray-300 font-semibold text-xl">{salaOnline.resultado_actual.segundo}</p>
                      <p className="text-gray-400 text-sm">
                        {salaOnline.respuestas_ronda?.[salaOnline.resultado_actual.segundo] && (
                          <span>"{salaOnline.respuestas_ronda[salaOnline.resultado_actual.segundo]}"</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Todas las respuestas */}
                <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-sm mb-3 text-center">Todas las respuestas:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(salaOnline.respuestas_ronda || {}).map(([jugador, respuesta]) => (
                      <div 
                        key={jugador} 
                        className={`p-2 rounded-lg text-center ${
                          jugador === salaOnline.resultado_actual.ganador 
                            ? 'bg-yellow-600/30 border border-yellow-500 text-yellow-300' 
                            : jugador === salaOnline.resultado_actual.segundo
                              ? 'bg-gray-600/30 border border-gray-500 text-gray-300'
                              : 'bg-gray-700/30 text-gray-400'
                        }`}
                      >
                        <p className="font-semibold text-sm">{jugador}</p>
                        <p className="text-xs opacity-80">"{respuesta}"</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Comentario del Master */}
                {salaOnline.resultado_actual.comentario_master && (
                  <div className="bg-purple-900/40 rounded-xl p-4 border border-purple-500">
                    <p className="text-purple-300 text-center italic text-lg">
                      🎭 <span className="text-purple-200">&ldquo;{salaOnline.resultado_actual.comentario_master}&rdquo;</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Terminada - Revelación ÉPICA */}
          {salaOnline.estado === 'terminada' && (
            <Card className="bg-gradient-to-br from-yellow-600 via-orange-500 to-red-600 mb-4 border-4 border-yellow-400 shadow-2xl shadow-yellow-500/50">
              <CardContent className="p-8 text-center">
                <div className="text-7xl mb-4 animate-bounce">🏆</div>
                <p className="text-white/80 text-xl mb-2">¡CAMPEÓN DE WORDS!</p>
                <p className="text-5xl font-bold text-white mb-4 animate-pulse">{salaOnline.ganador_final}</p>
                <div className="flex justify-center gap-4 text-4xl">
                  <span>🎉</span><span>🎊</span><span>🥳</span><span>🎉</span>
                </div>
                
                {/* Puntuaciones finales */}
                <div className="bg-black/30 rounded-xl p-4 mt-6">
                  <p className="text-white/80 mb-3">Puntuación Final:</p>
                  <div className="space-y-2">
                    {Object.entries(salaOnline.jugadores)
                      .sort(([,a], [,b]) => b.puntos - a.puntos)
                      .map(([jugador, stats], index) => (
                        <div 
                          key={jugador}
                          className={`flex justify-between items-center p-2 rounded-lg ${
                            index === 0 
                              ? 'bg-yellow-500/40 border border-yellow-400 text-xl font-bold' 
                              : 'bg-white/10 text-sm opacity-70'
                          }`}
                        >
                          <span className="text-white flex items-center gap-2">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                            {jugador}
                          </span>
                          <span className="text-white">{stats.puntos} pts</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Button variant="outline" className="w-full border-gray-600" onClick={cerrarSalaOnline}>
            Salir de la Sala
          </Button>
        </div>
      </div>
    );
  }

  // Vista de partida activa (local)
  if (partidaActual && palabraActual) {
    const categoria = CATEGORIAS.find(c => c.id === partidaActual.categoria);
    
    // PANTALLA DE EXPECTACIÓN - Revelación del ganador
    if (showReveal) {
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
          <div className="text-center animate-pulse">
            <div className="text-6xl sm:text-8xl mb-4">🎭</div>
            <h2 className="text-2xl sm:text-4xl font-bold text-gold mb-2">¿Quién será el ganador?</h2>
            <p className="text-lg sm:text-2xl text-gray-400">Preparando la revelación...</p>
            <div className="flex justify-center gap-3 mt-6">
              <div className="w-3 h-3 bg-gold rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
              <div className="w-3 h-3 bg-gold rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
              <div className="w-3 h-3 bg-gold rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar />
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-2xl">
          {/* Header compacto para móvil */}
          <div className="flex items-center justify-between mb-3 sm:mb-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{categoria?.emoji}</span>
              <span className="text-lg sm:text-xl font-bold text-gold">Words</span>
            </div>
            <div className="bg-gray-800 px-3 py-1 rounded-full">
              <span className="text-sm text-gray-300">Ronda {palabraActual.ronda}/{palabraActual.total_rondas}</span>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-gray-400 hover:text-white"
              onClick={() => {
                setPartidaActual(null);
                setPalabraActual(null);
                setResultado(null);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Puntuaciones compactas - solo nombres */}
          <div className="flex justify-center gap-2 mb-3 flex-wrap">
            {Object.entries(partidaActual.jugadores).map(([jugador, stats]) => (
              <div key={jugador} className="bg-gray-800/70 px-3 py-1 rounded-full text-center">
                <span className="text-white text-sm font-medium">{jugador}</span>
                <span className="text-gray-500 text-xs ml-1">???</span>
              </div>
            ))}
          </div>
          
          {/* Leyenda de puntos - más compacta */}
          <div className="text-center mb-3">
            <p className="text-[10px] sm:text-xs text-gray-500">
              <span className="text-red-400">❌0</span> • 
              <span className="text-yellow-400">⚠️1-4</span> • 
              <span className="text-green-400">✅5-8</span> • 
              <span className="text-purple-400">🌟9-10</span> • 
              <span className="text-blue-400">🏰11</span> • 
              <span className="text-gold">👑15</span>
            </p>
          </div>

          {/* Palabra - MÁS COMPACTA EN MÓVIL */}
          {!resultado && (
            <Card className="bg-gradient-to-br from-purple-900 to-indigo-900 border-purple-500 mb-4">
              <CardContent className="p-4 sm:p-8 text-center">
                <p className="text-purple-300 text-xs sm:text-sm mb-1">La palabra es...</p>
                <h2 className="text-3xl sm:text-5xl font-bold text-white uppercase tracking-wider">
                  {palabraActual.palabra}
                </h2>
                <p className="text-purple-300 text-xs mt-2">
                  ¡Escribe algo creativo!
                </p>
              </CardContent>
            </Card>
          )}

          {/* Respuestas */}
          {!resultado && (
            <Card className="bg-gray-900 border-gray-700 mb-4">
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-white text-base sm:text-lg">Respuestas</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0 space-y-3">
                {Object.keys(respuestas).map(jugador => (
                  <div key={jugador} className="flex gap-2 items-center">
                    <Label className="w-20 sm:w-24 text-white text-sm truncate">{jugador}</Label>
                    <Input
                      value={respuestas[jugador]}
                      onChange={(e) => setRespuestas({ ...respuestas, [jugador]: e.target.value })}
                      placeholder="Tu palabra..."
                      className="bg-gray-800 border-gray-600 text-white text-sm"
                    />
                  </div>
                ))}
                
                <Button 
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 mt-2"
                  onClick={handleJuzgar}
                  disabled={juzgando}
                  size="sm"
                >
                  {juzgando ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Juzgando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      ¡Juzgar!
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Resultado de la ronda */}
          {resultado && (
            <Card className="bg-gray-900 border-gray-700 mb-4">
              <CardHeader className="bg-gradient-to-r from-yellow-600 to-orange-600 p-3 sm:p-6">
                <CardTitle className="text-white text-center text-base sm:text-lg">
                  🎯 Resultado Ronda {resultado.ronda}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 space-y-3">
                <p className="text-center text-gray-400 text-sm">
                  Palabra: <span className="text-white font-bold uppercase">{resultado.palabra_original}</span>
                </p>

                {/* Ganadores - compacto */}
                <div className="flex justify-center gap-4">
                  {resultado.ganador && (
                    <div className="text-center">
                      <p className="text-2xl sm:text-4xl">🥇</p>
                      <p className="text-yellow-400 font-bold text-sm sm:text-xl">{resultado.ganador}</p>
                    </div>
                  )}
                  {resultado.segundo && (
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl">🥈</p>
                      <p className="text-gray-300 font-semibold text-sm">{resultado.segundo}</p>
                    </div>
                  )}
                </div>

                {/* Análisis - más compacto */}
                <div className="space-y-2 mt-3">
                  {resultado.analisis?.map((a, i) => (
                    <div 
                      key={i} 
                      className={`p-2 rounded-lg text-sm ${
                        a.puntuacion >= 15 ? 'bg-gradient-to-r from-yellow-600/30 to-purple-600/30 border-2 border-yellow-400 animate-pulse' :
                        a.puntuacion >= 11 ? 'bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border-2 border-blue-400' :
                        a.puntuacion > 0 ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-white truncate">{a.jugador}: &ldquo;{a.palabra}&rdquo;</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {a.rango_especial === 'MASTER OF WORDS' && (
                            <span className="bg-gradient-to-r from-yellow-400 to-purple-500 text-black px-1.5 py-0.5 rounded-full text-[10px] font-bold animate-bounce">
                              👑 MASTER OF WORDS
                            </span>
                          )}
                          {a.rango_especial === 'CABALLERO WORDS' && (
                            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 text-black px-2 py-1 rounded-full text-xs font-bold">
                              🏰 CABALLERO WORDS
                            </span>
                          )}
                          <span className={a.valida ? 'text-green-400' : 'text-red-400'}>
                            {a.puntuacion !== undefined ? `${a.puntuacion} pts` : (a.valida ? '✓' : '✗')}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{a.razon}</p>
                    </div>
                  ))}
                </div>

                {/* Comentario del Master */}
                {resultado.comentario_master && (
                  <div className="bg-purple-900/30 border border-purple-700 p-4 rounded-lg mt-4">
                    <p className="text-purple-300 text-sm">💬 El Master dice:</p>
                    <p className="text-white italic">&ldquo;{resultado.comentario_master}&rdquo;</p>
                  </div>
                )}

                {/* Botones */}
                <div className="flex flex-col gap-3 mt-6">
                  {!resultado.partida_terminada ? (
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={siguienteRonda}
                      >
                        Siguiente Ronda →
                      </Button>
                      <Button variant="outline" className="border-gray-600" onClick={cerrarPartida}>
                        Salir
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Resultado final ÉPICO */}
                      <div className="bg-gradient-to-r from-yellow-600 via-orange-500 to-red-500 p-8 rounded-xl text-center">
                        <div className="text-6xl mb-4">🏆</div>
                        <p className="text-white/80 text-lg mb-2">¡GANADOR!</p>
                        <p className="text-5xl font-bold text-white animate-pulse">{resultado.ganador_final}</p>
                        <div className="flex justify-center gap-4 mt-4">
                          <span className="text-4xl">🎉</span>
                          <span className="text-4xl">🎊</span>
                          <span className="text-4xl">🥳</span>
                        </div>
                      </div>
                      
                      {/* Puntuaciones finales - ganador grande, demás pequeños */}
                      <div className="bg-gray-800 rounded-xl p-4 mt-4">
                        <p className="text-gold font-bold text-center mb-3">📊 Puntuación Final</p>
                        <div className="space-y-2">
                          {Object.entries(partidaActual.jugadores)
                            .sort(([,a], [,b]) => b.puntos - a.puntos)
                            .map(([jugador, stats], index) => (
                              <div 
                                key={jugador}
                                className={`flex justify-between items-center p-3 rounded-lg transition-all ${
                                  index === 0 
                                    ? 'bg-gradient-to-r from-yellow-600/40 to-orange-600/40 border-2 border-yellow-500 scale-105 transform' 
                                    : 'bg-gray-700/30 border border-gray-700 opacity-70'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={index === 0 ? 'text-3xl' : 'text-xl'}>
                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                                  </span>
                                  <span className={`font-semibold ${index === 0 ? 'text-white text-xl' : 'text-gray-400 text-sm'}`}>
                                    {jugador}
                                  </span>
                                </div>
                                <span className={`font-bold ${index === 0 ? 'text-yellow-400 text-2xl' : 'text-gray-500 text-sm'}`}>
                                  {stats.puntos} pts
                                </span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                      
                      <Button variant="outline" className="border-gold text-gold hover:bg-gold/20 mt-4" onClick={cerrarPartida}>
                        Cerrar Partida
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Vista principal - lista de partidas
  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        {/* Banner de publicidad */}
        <AnuncioBanner />
        
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2">
              <MessageSquare className="h-8 w-8" /> Words
            </h1>
            <p className="text-gray-400">El juego de las palabras creativas</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={() => setShowReglas(true)}>
              ❓ Reglas
            </Button>
            <Button variant="outline" className="border-green-600 text-green-400 hover:bg-green-600/20" onClick={() => setShowOnline(true)}>
              <Wifi className="h-5 w-5 mr-2" /> Online
            </Button>
            <Button className="btn-primary-mk" onClick={() => setShowCrear(true)}>
              <Plus className="h-5 w-5 mr-2" /> Local
            </Button>
          </div>
        </div>

        {/* Categorías */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {CATEGORIAS.map(cat => (
            <Card 
              key={cat.id} 
              className="bg-gray-900 border-gray-700 hover:border-gold transition-colors cursor-pointer"
              onClick={() => {
                setFormData({ ...formData, categoria: cat.id });
                setShowCrear(true);
              }}
            >
              <CardContent className="p-6 text-center">
                <span className="text-4xl">{cat.emoji}</span>
                <h3 className="text-white font-bold mt-2">{cat.nombre}</h3>
                <p className="text-gray-400 text-sm">{cat.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal Reglas */}
        <Dialog open={showReglas} onOpenChange={setShowReglas}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold text-xl">📜 Reglas de Words</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="font-bold text-white mb-2">🎯 Objetivo</h3>
                <p className="text-gray-300">Escribe una palabra CREATIVA relacionada con la palabra dada, pero que NO sea obvia.</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="font-bold text-white mb-2">📊 Sistema de Puntos</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">0 pts</span>
                    <span><strong>Descalificado:</strong> Sinónimos, partes, materiales, acciones directas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400 font-bold">1-3 pts</span>
                    <span><strong>Débil:</strong> Conexión predecible, la mayoría lo pensaría</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 font-bold">4-6 pts</span>
                    <span><strong>Buena:</strong> Conexión lógica pero no obvia</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 font-bold">7-9 pts</span>
                    <span><strong>Excelente:</strong> Creativa, inesperada pero lógica</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 font-bold">10+ pts</span>
                    <span><strong>🏰 Caballero Words:</strong> Conexión brillante</span>
                  </li>
                </ul>
              </div>

              <div className="bg-purple-900/30 border border-purple-700 p-4 rounded-lg">
                <h3 className="font-bold text-purple-300 mb-2">👑 Master of Words</h3>
                <p className="text-gray-300">Si tu palabra es TAN perfecta que la IA la habría elegido, recibes el título de Master of Words con celebración especial.</p>
              </div>

              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="font-bold text-white mb-2">🏆 Puntos por Ronda</h3>
                <ul className="text-gray-300">
                  <li>🥇 <strong>Ganador:</strong> +3 puntos</li>
                  <li>🥈 <strong>Segundo:</strong> +1 punto</li>
                </ul>
              </div>

              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="font-bold text-white mb-2">💡 Ejemplo</h3>
                <p className="text-gray-400 mb-2">Palabra: <span className="text-white font-bold">LIBRO</span></p>
                <ul className="text-gray-300 space-y-1">
                  <li>❌ páginas, leer, papel → Descalificado (obvias)</li>
                  <li>⚠️ conocimiento → Débil (2 pts)</li>
                  <li>✅ refugio → Buena (5 pts)</li>
                  <li>🌟 inmortalidad → Excelente (8 pts)</li>
                </ul>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Partidas recientes */}
        <h2 className="text-xl font-bold text-white mb-4">Partidas Recientes</h2>
        {partidas.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay partidas aún. ¡Crea una!</p>
        ) : (
          <div className="grid gap-4">
            {partidas.map(partida => {
              const cat = CATEGORIAS.find(c => c.id === partida.categoria);
              return (
                <Card key={partida.id} className="bg-gray-900 border-gray-700">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{cat?.emoji}</span>
                      <div>
                        <p className="text-white font-semibold">{cat?.nombre}</p>
                        <p className="text-gray-400 text-sm">
                          {Object.keys(partida.jugadores).join(' vs ')} • 
                          {partida.estado === 'activa' ? ` Ronda ${partida.ronda_actual}/${partida.num_rondas}` : ' Terminada'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {partida.estado === 'terminada' && partida.ganador_final && (
                        <span className="text-yellow-400 flex items-center gap-1">
                          <Trophy className="h-4 w-4" /> {partida.ganador_final}
                        </span>
                      )}
                      {partida.estado === 'activa' && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => iniciarPartida(partida.id)}>
                          Continuar
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-900/30" onClick={() => eliminarPartida(partida.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialog Crear Partida */}
        <Dialog open={showCrear} onOpenChange={setShowCrear}>
          <DialogContent className="bg-gray-900 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gold">Nueva Partida de Words</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.emoji} {cat.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Número de Rondas</Label>
                <Select 
                  value={formData.num_rondas.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, num_rondas: parseInt(v) })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {[3, 5, 7, 10].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} rondas</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Jugadores</span>
                  <Button size="sm" variant="ghost" className="text-gold h-6" onClick={addJugador}>
                    <Plus className="h-4 w-4" /> Añadir
                  </Button>
                </Label>
                {formData.jugadores.map((jugador, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={jugador}
                      onChange={(e) => {
                        const newJugadores = [...formData.jugadores];
                        newJugadores[i] = e.target.value;
                        setFormData({ ...formData, jugadores: newJugadores });
                      }}
                      placeholder={`Jugador ${i + 1}`}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    {formData.jugadores.length > 2 && (
                      <Button size="sm" variant="ghost" className="text-red-400 px-2" onClick={() => removeJugador(i)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Button 
                className="w-full btn-primary-mk" 
                onClick={handleCrearPartida}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : '¡Empezar Partida!'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Online */}
        <Dialog open={showOnline} onOpenChange={setShowOnline}>
          <DialogContent className="bg-gray-900 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Wifi className="h-5 w-5" /> Words Online
              </DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="crear" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-800 mb-4">
                <TabsTrigger value="crear">Crear Sala</TabsTrigger>
                <TabsTrigger value="unirse">Unirse</TabsTrigger>
              </TabsList>
              
              <TabsContent value="crear" className="space-y-4">
                <div className="space-y-2">
                  <Label>Tu nombre</Label>
                  <Input
                    value={nombreJugador}
                    onChange={(e) => setNombreJugador(e.target.value)}
                    placeholder="Escribe tu nombre..."
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {CATEGORIAS.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.emoji} {cat.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rondas</Label>
                  <Select value={formData.num_rondas.toString()} onValueChange={(v) => setFormData({ ...formData, num_rondas: parseInt(v) })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {[3, 5, 7, 10].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} rondas</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={crearSalaOnline} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin" /> : 'Crear Sala'}
                </Button>
              </TabsContent>
              
              <TabsContent value="unirse" className="space-y-4">
                <div className="space-y-2">
                  <Label>Tu nombre</Label>
                  <Input
                    value={nombreJugador}
                    onChange={(e) => setNombreJugador(e.target.value)}
                    placeholder="Escribe tu nombre..."
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código de sala</Label>
                  <Input
                    value={codigoSala}
                    onChange={(e) => setCodigoSala(e.target.value.toUpperCase())}
                    placeholder="Ej: ABC123"
                    className="bg-gray-800 border-gray-700 font-mono text-lg text-center uppercase"
                    maxLength={6}
                  />
                </div>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={unirseSala} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin" /> : 'Unirse a Sala'}
                </Button>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Words;
